import React from 'react';
import {Blocker, useLocation} from 'react-router-dom';
import {Broker} from '../../types/Broker';
import {BrokerReportShareRecord} from '../../types/BrokerReportShareRecord';
import {ApiUtility} from "../common/ApiUtility";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {Constants} from "../common/Constants";
import {DateUtility} from "../common/DateUtility";
import {useModelFormEngine} from "../common/hooks/useModelFormEngine";
import {LeaveWithoutSavingWarningModal} from "../common/LeaveWithoutSavingWarningModal";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {Modal} from "../common/Modal";
import {PhoneUtility} from "../common/PhoneUtility";
import {PillButton} from "../common/PillButton";

interface ShareBrokerPlanModalProps {
    channel: 'text' | 'email';
    broker: Broker | undefined;
    dateString: string;
    planLinkUrl: string;
    close: () => void;
    onShared: (shareRecords: BrokerReportShareRecord[]) => void;
}

interface ShareBrokerPlanFormState {
    recipient: string;
    note: string;
    sending: boolean;
    confirmingCancel: boolean;
}

export let ShareBrokerPlanModal = function (props: ShareBrokerPlanModalProps) {
    const location = useLocation();
    const sendingByEmail = props.channel === 'email';
    const brokerName = props.broker ? props.broker.name : '';

    function initialRecipient() {
        if (sendingByEmail) {
            return props.broker?.external_contact?.email || '';
        }
        return PhoneUtility.dbPhoneToParenthesesDisplayPhone(props.broker?.external_contact?.phone) || '';
    }

    // Assembled in the same order as `broker_reports_helper.rb#broker_report_share_message_lines`, so the preview and the message the broker actually receives cannot drift apart.
    function messageThatWillBeSent() {
        const displayDate = DateUtility.dbDateToBrokerShareMessageDate(props.dateString);
        const messageLines = [`Berger Contractors - truck plan for ${displayDate} (${brokerName}).`];
        if (formEngine.dataStore.data().note.trim()) {
            messageLines.push(formEngine.dataStore.data().note);
        }
        messageLines.push(`View Plan: ${props.planLinkUrl}`);
        if (sendingByEmail) {
            return `Subject: Berger truck plan - ${displayDate}\n\n${messageLines.join('\n')}`;
        }
        return messageLines.join('\n');
    }

    function validateForm(handleValidationResult: Function) {
        return new Promise(function (resolve) {
            const validationErrors: string[] = [];
            const invalidFields: string[] = [];
            if (!formEngine.dataStore.data().recipient.trim()) {
                validationErrors.push(sendingByEmail ? 'An email address is required.' : 'A mobile number is required.');
                invalidFields.push('recipient');
            }
            handleValidationResult(validationErrors, invalidFields, resolve);
        });
    }

    function save(saveData: ShareBrokerPlanFormState, disableNavigationBlocker: Function) {
        formEngine.dataStore.updateAndRender({sending: true});
        const shareData = {recipient: saveData.recipient, note: saveData.note};
        const brokerId = props.broker ? props.broker.id : '';
        const shareRequest = sendingByEmail
            ? ApiUtility.shareBrokerReportByEmail(brokerId, props.dateString, shareData)
            : ApiUtility.shareBrokerReportByText(brokerId, props.dateString, shareData);

        shareRequest
            .then(function (sharingState: { shareRecords: BrokerReportShareRecord[] }) {
                disableNavigationBlocker();
                props.onShared(sharingState.shareRecords);
                MessageDisplayerUtility.success(`Successfully shared the plan.`, Constants.dispatchOrderMessageAdditionalConfig);
                props.close();
            })
            .catch(function (error: object) {
                // The share is left unrecorded and the modal open, so the dispatcher can correct the recipient and try again.
                formEngine.dataStore.updateAndRender({sending: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while sharing the plan.`, Constants.dispatchOrderMessageAdditionalConfig);
            });
    }

    const formEngine = useModelFormEngine<ShareBrokerPlanFormState>(
        {recipient: initialRecipient(), note: '', sending: false, confirmingCancel: false},
        undefined,
        undefined,
        validateForm,
        undefined,
        save,
        Constants.dispatchOrderMessageAdditionalConfig
    );

    function handleClickSend() {
        if (formEngine.dataStore.data().sending) {
            return;
        }
        formEngine.onClickSave();
    }

    function handleClickCancel() {
        if (formEngine.dataStore.data().saved) {
            props.close();
            return;
        }
        formEngine.dataStore.updateAndRender({confirmingCancel: true});
    }

    // Closing the modal on a typed note is not a navigation, so the application's leave-without-saving warning is driven here by a blocker of the same shape the form engine's own navigation blocker has.
    const cancelBlocker: Blocker = formEngine.dataStore.data().confirmingCancel
        ? {
            state: "blocked",
            location: location,
            proceed: function () {
                formEngine.disableNavigationBlocker();
                props.close();
            },
            reset: function () {
                formEngine.dataStore.updateAndRender({confirmingCancel: false});
            },
        }
        : {state: "unblocked", proceed: undefined, reset: undefined, location: undefined};

    return (
        <Modal
            boxClass="share-broker-plan-modal"
            headerText={sendingByEmail ? 'SHARE PLAN BY EMAIL' : 'SHARE PLAN BY TEXT'}
            onClickCloseOption={handleClickCancel}
        >
            <div className="share-broker-plan-modal-body">
                <div className="common-form-field share-broker-plan-modal-field">
                    <div className="common-form-field-label">{sendingByEmail ? 'EMAIL ADDRESS' : 'MOBILE NUMBER'}</div>
                    <div className="common-form-field-input-container">
                        <div className="common-form-field-input-container-content">
                            <input
                                className={formEngine.getFormFieldClasses(
                                    "common-form-input share-broker-plan-modal-recipient-input",
                                    "recipient"
                                )}
                                value={formEngine.dataStore.data().recipient}
                                onChange={(event) => formEngine.handleTextFieldChange("recipient", event)}
                            />
                        </div>
                    </div>
                    <ConditionalRenderer if={!sendingByEmail} renderer={() => (
                        <div className="common-form-field-sublabel">{`${brokerName} primary contact`}</div>
                    )}/>
                </div>

                <div className="common-form-field share-broker-plan-modal-field">
                    <div className="common-form-field-label">NOTE (OPTIONAL)</div>
                    <div className="common-form-field-input-container">
                        <div className="common-form-field-input-container-content">
                            <textarea
                                className="common-form-input share-broker-plan-modal-note-input"
                                placeholder="Add a short note for the broker"
                                value={formEngine.dataStore.data().note}
                                onChange={(event) => formEngine.handleTextFieldChange("note", event)}
                            />
                        </div>
                    </div>
                </div>

                <div className="common-form-field share-broker-plan-modal-field message-field">
                    <div className="common-form-field-label">MESSAGE THAT WILL BE SENT</div>
                    <div className="share-broker-plan-modal-message-block">{messageThatWillBeSent()}</div>
                </div>

                <div className="share-broker-plan-modal-options">
                    <div className="share-broker-plan-modal-option">
                        <PillButton
                            onClick={handleClickSend}
                            buttonClasses="solid-black-button share-broker-plan-modal-send-button"
                            buttonText="SEND"
                            disabled={formEngine.dataStore.data().sending}
                        />
                    </div>
                    <div className="share-broker-plan-modal-option">
                        <PillButton
                            onClick={handleClickCancel}
                            buttonClasses="black-border-button share-broker-plan-modal-cancel-button"
                            buttonText="CANCEL"
                        />
                    </div>
                </div>

                <LeaveWithoutSavingWarningModal
                    navigationBlocker={
                        formEngine.dataStore.data().confirmingCancel ? cancelBlocker : formEngine.navigationBlocker
                    }
                />
            </div>
        </Modal>
    );
};
